import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import _ from 'lodash';
import styled, { css } from 'styled-components';

import Box from '@g4/boneyard/lib/Box';

import { TrackClick } from '@g4/trackster';
import hooks from '@g4/automation-hooks';

import incrementIcon from '../images/increment-icon.svg';
import incrementIconDisabled from '../images/increment-icon-disabled.svg';
import decrementIcon from '../images/decrement-icon.svg';
import decrementIconDisabled from '../images/decrement-icon-disabled.svg';

const BtnWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  ${props =>
    props.decrement &&
    css`
      order: -1;
    `};
`;

const StyledButton = styled.button.attrs({ type: 'button' })`
  height: auto;
  display: flex;
  &:focus,
  &:hover:not(:disabled) {
    border-radius: 50%;
    outline: 0;
    box-shadow: 0 0 0 4px ${props => props.theme.colors.hob.input.focused};
  }
`;

const StyledInput = styled.input`
  width: 2rem;
  border: none;
  vertical-align: text-bottom;
  text-align: center;
  font-size: 1rem;
  font-family: Montserrat, Arial, sans-serif;
  color: #000;
`;

/** @visibleName Quantity Selector */
export class QuantitySelector extends PureComponent {
  static propTypes = {
    automationHookDecrement: PropTypes.string,
    automationHookIncrement: PropTypes.string,
    automationHookValue: PropTypes.string,
    decrementButtonAriaLabel: PropTypes.string.isRequired,
    incrementButtonAriaLabel: PropTypes.string.isRequired,
    quantitySelectorAriaLabel: PropTypes.string.isRequired,
    incrementClickAction: PropTypes.string,
    decrementClickAction: PropTypes.string,
    decrementClickLabel: PropTypes.string,
    incrementClickLabel: PropTypes.string,
    valueSelectedAriaLabel: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    max: PropTypes.number,
    min: PropTypes.number,
    value: PropTypes.number,
    disabled: PropTypes.bool,
    className: PropTypes.string,
  };

  static defaultProps = {
    onChange: Function.prototype,
    max: 99,
    min: 0,
    value: 0,
    disabled: false,
  };

  constructor(props) {
    super(props);

    let value;

    if (
      this.props.value > this.props.max ||
      this.props.value < this.props.min
    ) {
      value = this.props.min;
    } else {
      value = this.props.value;
    }

    const inputID = _.uniqueId('QuantitySelector-');

    this.state = {
      value,
      inputID,
    };

    this.onDecrement = this.onDecrement.bind(this);
    this.onIncrement = this.onIncrement.bind(this);
    this.assignInput = this.assignInput.bind(this);
  }

  assignInput(el) {
    this.input = el;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      ...nextProps,
    });
  }

  onDecrement() {
    var value = this.state.value - 1;
    this.props.onChange(value);
  }

  onIncrement() {
    var value = this.state.value + 1;
    this.props.onChange(value);
  }

  renderDecrementButton() {
    const button = (
      <BtnWrapper decrement={true}>
        <StyledButton
          aria-label={this.props.decrementButtonAriaLabel}
          onClick={this.onDecrement}
          disabled={this.props.disabled || this.state.value <= this.props.min}
          aria-controls={this.state.inputID}
          {...hooks.hook(this.props.automationHookDecrement)}
        >
          <img
            src={
              this.props.disabled || this.state.value <= this.props.min
                ? decrementIconDisabled
                : decrementIcon
            }
            alt=""
          />
        </StyledButton>
      </BtnWrapper>
    );
    if (this.props.decrementClickAction && this.props.decrementClickLabel) {
      return (
        <TrackClick
          action={this.props.decrementClickAction}
          label={this.props.decrementClickLabel}
        >
          {button}
        </TrackClick>
      );
    } else {
      return button;
    }
  }

  renderIncrementButton() {
    const button = (
      <BtnWrapper>
        <StyledButton
          aria-label={this.props.incrementButtonAriaLabel}
          onClick={this.onIncrement}
          disabled={this.props.disabled || this.state.value >= this.props.max}
          aria-controls={this.state.inputID}
          {...hooks.hook(this.props.automationHookIncrement)}
        >
          <img
            src={
              this.props.disabled || this.state.value >= this.props.max
                ? incrementIconDisabled
                : incrementIcon
            }
            alt=""
          />
        </StyledButton>
      </BtnWrapper>
    );

    if (this.props.incrementClickAction && this.props.incrementClickLabel) {
      return (
        <TrackClick
          action={this.props.incrementClickAction}
          label={this.props.incrementClickLabel}
        >
          {button}
        </TrackClick>
      );
    }
    return button;
  }

  render() {
    return (
      <div
        className={this.props.className}
        aria-label={this.props.quantitySelectorAriaLabel}
      >
        <Box
          d="flex"
          role="alert"
          aria-live="assertive"
          {...hooks.hook(this.props.automationHookValue)}
        >
          <StyledInput
            {...hooks.hook(this.props.automationHookValue)}
            ref={this.assignInput}
            id={this.state.inputID}
            active={this.props.min !== this.state.value}
            aria-label={this.props.valueSelectedAriaLabel}
            value={this.props.value}
            readOnly
          />
        </Box>
        {this.renderDecrementButton()}
        {this.renderIncrementButton()}
      </div>
    );
  }
}

export default styled(QuantitySelector)`
  display: flex;
  border-radius: 8px;
  padding: 0 ${props => props.theme.spacing(2)};
  background-color: #ffffff;
  width: 100%;
  height: 50px;
  justify-content: space-between;
  ${props =>
    props.border &&
    css`
      border: ${props.border};
    `}
`;
